import { Suspense, useEffect, useMemo, useRef, useState } from "react";
import {
    Alert,
    Button,
    Card,
    ConfigProvider,
    Popconfirm,
    Statistic,
    Switch,
    Tag,
    Tooltip,
    message,
} from "antd";
import {
    ReloadOutlined,
    WarningFilled,
    InfoCircleOutlined,
    RedoOutlined,
} from "@ant-design/icons";
import container from "@/lib/di/container";
import {
    FilterBar,
    FilterInputTemplate,
    FilterInputType,
} from "../../components/filter/FilterBar";
import {
    DataTable,
    FilterSearchPayload,
} from "../../components/table/DataTable";
import { DetailModal } from "./modals/DetailModal";
import { PaymentOutboxRepo } from "@/core/repos/tables/payment-outbox.repo";
import { WaterFactoryRepo } from "@/core/repos/tables/water-factory.repo";
import {
    PAYMENT_OUTBOX_CHANNELS,
    PaymentOutboxItemModel,
    PaymentOutboxItemModelFactory,
    PaymentOutboxStatsModel,
    PaymentOutboxStatus,
} from "@/core/models/payment-outbox.model";
import { WaterFactoryModel } from "@/core/models/water-factory.model";
import {
    paymentOutboxStatusPipe,
    paymentOutboxStatusTagPipe,
} from "@/core/pipes/payment-outbox-status.pipe";
import { formatDateTimePipe } from "@/core/pipes/format-date.pipe";
import { formatCurrencyPipeText } from "@/core/pipes/format-currency.pipe";
import AuthRepo from "@/core/repos/auth.repo";
import {
    PERMISSIONS,
    PermissionClaim,
} from "@/core/enums/permissions.enum";
import { GlobalUtils } from "../../utils/global.utils";

const paymentOutboxRepo = container.get(PaymentOutboxRepo);
const waterFactoryRepo = container.get(WaterFactoryRepo);
const authRepo = container.get(AuthRepo);

const AUTO_REFRESH_MS = 10000;
const STUCK_PENDING_THRESHOLD_SECONDS = 300;
const PENDING_SPIKE_THRESHOLD = 50;
const PROCESSING_SPIKE_THRESHOLD = 5;

function PaymentOutbox() {
    const claims = authRepo.useClaims() as PermissionClaim[];
    const userDetail = authRepo.useDetails();
    const [filterMap, setFilterMap] = useState<Map<string, FilterSearchPayload>>(
        new Map()
    );
    const [selectedRows, setSelectedRows] = useState<PaymentOutboxItemModel[]>(
        []
    );
    const [modalData, setModalData] = useState<PaymentOutboxItemModel>(
        PaymentOutboxItemModelFactory.getEmpty()
    );
    const [isDetailModal, setDetailModal] = useState(false);
    const [stats, setStats] = useState<PaymentOutboxStatsModel | null>(null);
    const [statsError, setStatsError] = useState<string | null>(null);
    const [autoRefresh, setAutoRefresh] = useState<boolean>(true);
    const [retrying, setRetrying] = useState<boolean>(false);
    const [messageApi, contextHolder] = message.useMessage();

    // Cached changes signal so DataTable re-fetches when stats refreshed / retried
    const changesTick = paymentOutboxRepo.useChanges();

    const canViewAll = useMemo(() => {
        if (userDetail?.userName.toLowerCase() === "admin") return true;
        return GlobalUtils.checkPerm(
            claims,
            PERMISSIONS.PAYMENT_OUTBOX_VIEW_ALL.key
        );
    }, [claims, userDetail]);

    const canRetry = useMemo(() => {
        if (userDetail?.userName.toLowerCase() === "admin") return true;
        return GlobalUtils.checkPerm(
            claims,
            PERMISSIONS.PAYMENT_OUTBOX_RETRY.key
        );
    }, [claims, userDetail]);

    const factoriesRef = useRef<WaterFactoryModel[]>([]);
    const [filterTemplates, setFilterTemplates] = useState<FilterInputTemplate[]>(
        buildFilterTemplates(canViewAll, [])
    );

    useEffect(() => {
        if (canViewAll) {
            waterFactoryRepo.getAll().then((items) => {
                factoriesRef.current = items;
                setFilterTemplates(
                    buildFilterTemplates(canViewAll, items)
                );
            });
        } else {
            setFilterTemplates(buildFilterTemplates(false, []));
        }
    }, [canViewAll]);

    const fetchStats = async () => {
        const result = await paymentOutboxRepo.getStats();
        if (result) {
            setStats(result);
            setStatsError(null);
        } else {
            setStatsError(
                "Không kết nối được tới server. Sẽ thử lại tự động sau " +
                    AUTO_REFRESH_MS / 1000 +
                    " giây."
            );
        }
    };

    // Initial stats fetch
    useEffect(() => {
        fetchStats();
    }, []);

    // Auto-refresh stats + table
    useEffect(() => {
        if (!autoRefresh) return;
        const id = window.setInterval(() => {
            fetchStats();
            paymentOutboxRepo.refresh();
        }, AUTO_REFRESH_MS);
        return () => window.clearInterval(id);
    }, [autoRefresh]);

    // Re-fetch stats after any data change (retry, manual refresh)
    useEffect(() => {
        if (changesTick > 0) {
            fetchStats();
        }
    }, [changesTick]);

    async function onFilterChange(map: Map<string, FilterSearchPayload>) {
        setFilterMap(new Map(map));
    }

    function onRowSelect(rows: PaymentOutboxItemModel[]) {
        setSelectedRows([...rows]);
    }

    const showDetail = (record: PaymentOutboxItemModel) => {
        setModalData(record);
        setDetailModal(true);
    };

    const deadSelected = useMemo(
        () => selectedRows.filter((r) => r.status === PaymentOutboxStatus.Dead),
        [selectedRows]
    );

    const handleRetrySelected = async () => {
        if (deadSelected.length === 0) return;
        setRetrying(true);
        const result = await paymentOutboxRepo.retryDead(
            deadSelected.map((r) => r.id)
        );
        if (result) {
            const note =
                result.skipped > 0
                    ? ` (bỏ qua ${result.skipped} do không thuộc quyền)`
                    : "";
            messageApi.success(
                `Đã đưa ${result.retried}/${result.requested} giao dịch vào hàng đợi để xử lý lại${note}`
            );
            setSelectedRows([]);
        } else {
            messageApi.error("Thử lại thất bại - vui lòng thử làm mới trang");
        }
        setRetrying(false);
    };

    const handleManualRefresh = () => {
        fetchStats();
        paymentOutboxRepo.refresh();
    };

    const filterByStatus = (status: PaymentOutboxStatus) => {
        const newMap = new Map(filterMap);
        newMap.set("status", { key: "status", value: String(status) });
        setFilterMap(newMap);
    };

    const pendingSpike = (stats?.pending ?? 0) > PENDING_SPIKE_THRESHOLD;
    const processingSpike =
        (stats?.processing ?? 0) > PROCESSING_SPIKE_THRESHOLD;
    const stuckPending =
        (stats?.oldestPendingAgeSeconds ?? 0) >
        STUCK_PENDING_THRESHOLD_SECONDS;
    const deadCount = stats?.dead ?? 0;

    const healthMessage = stats?.healthWarning
        ? stats.healthWarning
        : stuckPending
        ? `Worker có thể bị stuck: giao dịch Pending cũ nhất đã chờ ${stats?.oldestPendingAgeSeconds}s`
        : null;

    return (
        <div className="px-4 py-6">
            {contextHolder}

            {/* Stats cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                <Card
                    size="small"
                    className={pendingSpike ? "border !border-red-400" : ""}
                    styles={{ body: { padding: 16 } }}
                >
                    <Statistic
                        title={
                            <div className="flex items-center gap-2">
                                <span>Đang chờ</span>
                                {pendingSpike && (
                                    <Tooltip title="Pending đang tăng nhanh">
                                        <Tag color="error">đang tăng</Tag>
                                    </Tooltip>
                                )}
                            </div>
                        }
                        value={stats?.pending ?? "-"}
                        valueStyle={{ color: "var(--warning-button)" }}
                    />
                    {stats?.oldestPendingAgeSeconds != null &&
                        stats.oldestPendingAgeSeconds > 0 && (
                            <p className="text-xs text-gray-500 mt-1">
                                cũ nhất: {stats.oldestPendingAgeSeconds}s
                                {stuckPending && (
                                    <span className="text-red-500 font-semibold">
                                        {" "}
                                        (stuck?)
                                    </span>
                                )}
                            </p>
                        )}
                </Card>

                <Card
                    size="small"
                    className={
                        processingSpike ? "border !border-yellow-400" : ""
                    }
                    styles={{ body: { padding: 16 } }}
                >
                    <Statistic
                        title={
                            <div className="flex items-center gap-2">
                                <span>Đang xử lý</span>
                                {processingSpike && (
                                    <Tooltip title="Nhiều worker đang chạy">
                                        <Tag color="warning">đông</Tag>
                                    </Tooltip>
                                )}
                            </div>
                        }
                        value={stats?.processing ?? "-"}
                        valueStyle={{ color: "var(--primary-button)" }}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                        đang chạy worker
                    </p>
                </Card>

                <Card size="small" styles={{ body: { padding: 16 } }}>
                    <Statistic
                        title="Hoàn thành"
                        value={stats?.done ?? "-"}
                        valueStyle={{ color: "var(--success-button)" }}
                    />
                    <p className="text-xs text-gray-500 mt-1">đã commit xong</p>
                </Card>

                <Card
                    size="small"
                    hoverable={deadCount > 0}
                    onClick={() =>
                        deadCount > 0 &&
                        filterByStatus(PaymentOutboxStatus.Dead)
                    }
                    className={
                        deadCount > 0 ? "border !border-red-500 cursor-pointer" : ""
                    }
                    styles={{ body: { padding: 16 } }}
                >
                    <Statistic
                        title={
                            <div className="flex items-center gap-2">
                                <span>Lỗi (Dead)</span>
                                {deadCount > 0 && (
                                    <Tooltip title="Click để lọc các giao dịch Dead">
                                        <Tag color="error">cần kiểm tra</Tag>
                                    </Tooltip>
                                )}
                            </div>
                        }
                        value={deadCount}
                        valueStyle={{
                            color:
                                deadCount > 0
                                    ? "var(--danger-button)"
                                    : undefined,
                        }}
                    />
                    {deadCount > 0 ? (
                        <p className="text-xs text-red-500 mt-1">
                            click để xem chi tiết
                        </p>
                    ) : (
                        <p className="text-xs text-gray-500 mt-1">không có</p>
                    )}
                </Card>
            </div>

            {/* Health warnings */}
            {statsError && (
                <Alert
                    className="mb-4"
                    type="error"
                    showIcon
                    icon={<WarningFilled />}
                    message={statsError}
                    action={
                        <Button size="small" onClick={fetchStats}>
                            Thử lại
                        </Button>
                    }
                />
            )}
            {!statsError && healthMessage && (
                <Alert
                    className="mb-4"
                    type="warning"
                    showIcon
                    message={healthMessage}
                />
            )}

            {/* Filter + actions */}
            <FilterBar
                setMap={setFilterMap}
                onChange={onFilterChange}
                templates={filterTemplates}
                map={filterMap}
                actions={[
                    <div
                        key="actions"
                        className="mt-6 flex items-center gap-2 flex-wrap"
                    >
                        <ConfigProvider
                            theme={{
                                components: {
                                    Switch: { colorPrimary: "#0B7DB6" },
                                },
                            }}
                        >
                            <span className="flex items-center gap-1 text-sm">
                                <Switch
                                    size="small"
                                    checked={autoRefresh}
                                    onChange={setAutoRefresh}
                                />
                                <span className="text-gray-600">
                                    Tự làm mới ({AUTO_REFRESH_MS / 1000}s)
                                </span>
                            </span>
                        </ConfigProvider>
                        <Tooltip title="Làm mới ngay">
                            <Button
                                icon={<ReloadOutlined />}
                                onClick={handleManualRefresh}
                            >
                                Làm mới
                            </Button>
                        </Tooltip>
                        {canRetry && (
                            <Popconfirm
                                title={`Thử lại ${deadSelected.length} giao dịch Dead?`}
                                description="Các giao dịch sẽ về Pending và worker xử lý lại trong < 2 giây."
                                onConfirm={handleRetrySelected}
                                okText="Thử lại"
                                cancelText="Huỷ"
                                disabled={deadSelected.length === 0}
                            >
                                <Button
                                    type="primary"
                                    icon={<RedoOutlined />}
                                    loading={retrying}
                                    disabled={deadSelected.length === 0}
                                    style={{ background: "var(--warning-button)" }}
                                >
                                    Thử lại ({deadSelected.length})
                                </Button>
                            </Popconfirm>
                        )}
                    </div>,
                ]}
            />

            <div className="mt-4 border-t border-dashed border-gray-300 pt-6">
                <DataTable<PaymentOutboxItemModel>
                    columns={[
                        {
                            dataIndex: "requestId",
                            name: "RequestId",
                        },
                        {
                            dataIndex: "bankTransactionId",
                            name: "BankTransactionId",
                            render: (_, record) => (
                                <div className="px-4 flex flex-col justify-center h-[60px]">
                                    <span
                                        className="truncate inline-block max-w-[180px]"
                                        title={record.bankTransactionId ?? ""}
                                    >
                                        {record.bankTransactionId ?? "-"}
                                    </span>
                                </div>
                            ),
                        },
                        { dataIndex: "channel", name: "Kênh" },
                        { dataIndex: "orderId", name: "OrderId" },
                        {
                            dataIndex: "amount",
                            name: "Số tiền (VNĐ)",
                            pipe: (v: number) => (
                                <div className="text-right">
                                    {formatCurrencyPipeText(v)}
                                </div>
                            ),
                        },
                        {
                            dataIndex: "status",
                            name: "Trạng thái",
                            pipe: (v: number) => paymentOutboxStatusTagPipe(v),
                        },
                        {
                            dataIndex: "attemptCount",
                            name: "Lần thử",
                            render: (_, record) => {
                                const danger =
                                    record.attemptCount > 0 &&
                                    record.status !== PaymentOutboxStatus.Done;
                                return (
                                    <div className="px-4 flex flex-col justify-center h-[60px]">
                                        <Tag color={danger ? "error" : "default"}>
                                            {record.attemptCount}
                                        </Tag>
                                    </div>
                                );
                            },
                        },
                        {
                            dataIndex: "createdAt",
                            name: "Tạo lúc",
                            pipe: (v: string) =>
                                v ? formatDateTimePipe(v) : "-",
                        },
                        {
                            dataIndex: "durationMs",
                            name: "Thời gian xử lý",
                            pipe: (v: number | null) =>
                                v != null ? `${v.toLocaleString()} ms` : "-",
                        },
                        {
                            dataIndex: "waterFactoryName",
                            name: "Nhà máy",
                            render: (_, record) => (
                                <div className="px-4 flex flex-col justify-center h-[60px]">
                                    <span
                                        className="truncate inline-block max-w-[180px]"
                                        title={record.waterFactoryName ?? ""}
                                    >
                                        {record.waterFactoryName ?? "-"}
                                    </span>
                                </div>
                            ),
                        },
                        {
                            name: "Thao tác",
                            render: (_, record) => (
                                <div className="flex flex-row px-1 items-center justify-center">
                                    <Tooltip title="Xem chi tiết">
                                        <Button
                                            type="text"
                                            icon={<InfoCircleOutlined />}
                                            onClick={() => showDetail(record)}
                                        />
                                    </Tooltip>
                                </div>
                            ),
                            fixed: "right",
                            width: 90,
                        },
                    ]}
                    repository={paymentOutboxRepo}
                    filterMap={filterMap}
                    onSelect={onRowSelect}
                    hideSelection={!canRetry}
                />
            </div>

            <DetailModal
                isModalOpen={isDetailModal}
                setIsModalOpen={setDetailModal}
                data={modalData}
                canRetry={canRetry}
            />
        </div>
    );
}

function buildFilterTemplates(
    canViewAll: boolean,
    factories: WaterFactoryModel[]
): FilterInputTemplate[] {
    const templates: FilterInputTemplate[] = [
        {
            dataIndex: "search",
            label: "Tìm kiếm",
            placeholder: "BankTx / OrderId / RequestId",
            type: FilterInputType.text,
        },
        {
            dataIndex: "status",
            label: "Trạng thái",
            type: FilterInputType.select,
            placeholder: "Tất cả",
            options: (
                [
                    {
                        value: PaymentOutboxStatus.Pending,
                        name: paymentOutboxStatusPipe(PaymentOutboxStatus.Pending),
                    },
                    {
                        value: PaymentOutboxStatus.Processing,
                        name: paymentOutboxStatusPipe(
                            PaymentOutboxStatus.Processing
                        ),
                    },
                    {
                        value: PaymentOutboxStatus.Done,
                        name: paymentOutboxStatusPipe(PaymentOutboxStatus.Done),
                    },
                    {
                        value: PaymentOutboxStatus.Dead,
                        name: paymentOutboxStatusPipe(PaymentOutboxStatus.Dead),
                    },
                ]
            ),
        },
        {
            dataIndex: "channel",
            label: "Kênh",
            type: FilterInputType.select,
            placeholder: "Tất cả",
            options: PAYMENT_OUTBOX_CHANNELS.map((c) => ({ value: c, name: c })),
        },
        {
            rangeIndex: { from: "fromDate", to: "toDate" },
            type: FilterInputType.dateRange,
            placeholder: "Từ ngày",
            secondaryPlaceholder: "Đến ngày",
            label: "Thời gian tạo",
            span: 2,
            showTime: true,
        },
    ];

    if (canViewAll) {
        templates.push({
            dataIndex: "waterFactoryId",
            label: "Nhà máy",
            type: FilterInputType.select,
            placeholder: "Tất cả nhà máy",
            options: factories.map((f) => ({ value: f.id, name: f.name })),
            span: 2,
        });
    }

    return templates;
}

export default function PaymentOutboxPage() {
    return (
        <Suspense>
            <PaymentOutbox />
        </Suspense>
    );
}
